import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Plus, Pencil, Trash2, ChevronLeft, ChevronRight, UserCircle,
  ArrowDownRight, Settings, X, RefreshCw, Wallet, Receipt
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const TODAY = new Date().toISOString().split("T")[0];
const MESES = [
  "Janeiro", "Fevereiro", "Marco", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];
const ANOS = Array.from({ length: 25 }, (_, i) => 2026 + i);
const FORMAS_PAGAMENTO = ["PIX", "Cartao de Credito", "Cartao de Debito", "Dinheiro", "Boleto", "Transferencia"];

const formatCurrency = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

const despesaSchema = z.object({
  banco: z.string().min(1, "Banco obrigatorio"),
  data: z.string().min(1, "Data obrigatoria"),
  valor: z.coerce.number().min(0),
  forma_pagamento: z.string().min(1, "Forma de pagamento obrigatoria"),
  categoria: z.string().optional().default(""),
  titular: z.string().optional().default(""),
  descricao: z.string().optional().default(""),
});

const categoriaSchema = z.object({
  nome: z.string().min(1, "Nome da categoria obrigatorio"),
});

export default function HubPessoalPage() {
  const { authAxios } = useAuth();
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [ano, setAno] = useState(now.getFullYear());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Data
  const [variaveis, setVariaveis] = useState([]);
  const [fixas, setFixas] = useState([]);
  const [categorias, setCategorias] = useState([]);

  // Dialog states
  const [dVariavel, setDVariavel] = useState(false);
  const [dFixa, setDFixa] = useState(false);
  const [dConfig, setDConfig] = useState(false);
  const [dConfigBancos, setDConfigBancos] = useState(false);
  const [novoBanco, setNovoBanco] = useState("");
  const [bancosPessoal, setBancosPessoal] = useState([]);
  const [dConfigTitulares, setDConfigTitulares] = useState(false);
  const [novoTitular, setNovoTitular] = useState("");
  const [titulares, setTitulares] = useState([]);
  const [editVariavel, setEditVariavel] = useState(null);
  const [editFixa, setEditFixa] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Filtro por titular para o detalhamento
  const [filtroTitular, setFiltroTitular] = useState("");

  // Forms
  const fVariavel = useForm({ resolver: zodResolver(despesaSchema), defaultValues: { banco: "", data: TODAY, valor: 0, forma_pagamento: "", categoria: "", titular: "", descricao: "" } });
  const fFixa = useForm({ resolver: zodResolver(despesaSchema), defaultValues: { banco: "", data: TODAY, valor: 0, forma_pagamento: "", categoria: "", titular: "", descricao: "" } });
  const fCategoria = useForm({ resolver: zodResolver(categoriaSchema), defaultValues: { nome: "" } });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = `mes=${mes}&ano=${ano}`;
      const [v, f, c, s, bp, tp] = await Promise.all([
        authAxios.get(`/hub-pessoal/variaveis?${params}`),
        authAxios.get(`/hub-pessoal/fixas?${params}`),
        authAxios.get("/hub-pessoal/categorias"),
        authAxios.get(`/hub-pessoal/stats?${params}`),
        authAxios.get("/hub-pessoal/config/bancos"),
        authAxios.get("/hub-pessoal/config/titulares"),
      ]);
      setVariaveis(v.data);
      setFixas(f.data);
      setCategorias(c.data);
      setStats(s.data);
      setBancosPessoal(bp.data);
      setTitulares(tp.data);
    } catch {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [authAxios, mes, ano]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleMesAnterior = () => {
    if (mes === 1) { setMes(12); setAno(ano - 1); } else { setMes(mes - 1); }
  };
  const handleMesProximo = () => {
    if (mes === 12) { setMes(1); setAno(ano + 1); } else { setMes(mes + 1); }
  };

  // Categoria CRUD
  const submitCategoria = async (data) => {
    try {
      await authAxios.post("/hub-pessoal/categorias", data);
      toast.success("Categoria criada!");
      fCategoria.reset({ nome: "" });
      const res = await authAxios.get("/hub-pessoal/categorias");
      setCategorias(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar categoria");
    }
  };

  const deleteCategoria = async (id) => {
    if (!window.confirm("Excluir esta categoria?")) return;
    try {
      await authAxios.delete(`/hub-pessoal/categorias/${id}`);
      toast.success("Categoria removida!");
      const res = await authAxios.get("/hub-pessoal/categorias");
      setCategorias(res.data);
    } catch { toast.error("Erro ao remover"); }
  };

  // Despesa submit helper
  const submitDespesa = async (tipo, data, editItem, setDialog) => {
    setSubmitting(true);
    try {
      if (editItem) {
        await authAxios.put(`/hub-pessoal/${tipo}/${editItem.id}`, data);
        toast.success("Atualizado!");
      } else {
        await authAxios.post(`/hub-pessoal/${tipo}`, { ...data, mes, ano });
        toast.success("Despesa adicionada!");
      }
      setDialog(false);
      fetchAll();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSubmitting(false); }
  };

  const deleteDespesa = async (tipo, id) => {
    if (!window.confirm("Excluir?")) return;
    try {
      await authAxios.delete(`/hub-pessoal/${tipo}/${id}`);
      toast.success("Removido!");
      fetchAll();
    } catch { toast.error("Erro ao remover"); }
  };

  // Form rendering helper for despesas
  const renderDespesaForm = (form) => (
    <>
      <div className="space-y-2">
        <Label>Banco</Label>
        <Select value={form.watch("banco") || ""} onValueChange={(v) => form.setValue("banco", v)}>
          <SelectTrigger data-testid="input-despesa-banco">
            <SelectValue placeholder="Selecione o banco" />
          </SelectTrigger>
          <SelectContent>
            {bancosPessoal.map((b) => (
              <SelectItem key={b.id} value={b.nome}>{b.nome}</SelectItem>
            ))}
            {bancosPessoal.length === 0 && (
              <div className="px-3 py-2 text-xs text-muted-foreground">Nenhum banco. Cadastre em Configuracoes.</div>
            )}
          </SelectContent>
        </Select>
        {form.formState.errors.banco && <p className="text-xs text-destructive">{form.formState.errors.banco.message}</p>}
      </div>
      <div className="space-y-2">
        <Label>Data</Label>
        <Input type="date" {...form.register("data")} data-testid="input-despesa-data" />
        {form.formState.errors.data && <p className="text-xs text-destructive">{form.formState.errors.data.message}</p>}
      </div>
      <div className="space-y-2">
        <Label>Valor (R$)</Label>
        <Input type="number" step="0.01" {...form.register("valor")} data-testid="input-despesa-valor" />
      </div>
      <div className="space-y-2">
        <Label>Forma de Pagamento</Label>
        <Select value={form.watch("forma_pagamento")} onValueChange={(v) => form.setValue("forma_pagamento", v)}>
          <SelectTrigger data-testid="select-forma-pagamento">
            <SelectValue placeholder="Selecione" />
          </SelectTrigger>
          <SelectContent>
            {FORMAS_PAGAMENTO.map((fp) => (
              <SelectItem key={fp} value={fp}>{fp}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {form.formState.errors.forma_pagamento && <p className="text-xs text-destructive">{form.formState.errors.forma_pagamento.message}</p>}
      </div>
      <div className="space-y-2">
        <Label>Categoria</Label>
        <Select value={form.watch("categoria") || ""} onValueChange={(v) => form.setValue("categoria", v)}>
          <SelectTrigger data-testid="select-categoria">
            <SelectValue placeholder="Selecione a categoria" />
          </SelectTrigger>
          <SelectContent>
            {categorias.map((cat) => (
              <SelectItem key={cat.id} value={cat.nome}>{cat.nome}</SelectItem>
            ))}
            {categorias.length === 0 && (
              <div className="px-3 py-2 text-xs text-muted-foreground">Nenhuma categoria. Crie em Configuracoes.</div>
            )}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Titular</Label>
        <Select value={form.watch("titular") || ""} onValueChange={(v) => form.setValue("titular", v)}>
          <SelectTrigger data-testid="select-titular">
            <SelectValue placeholder="Selecione o titular" />
          </SelectTrigger>
          <SelectContent>
            {titulares.map((t) => (
              <SelectItem key={t.id} value={t.nome}>{t.nome}</SelectItem>
            ))}
            {titulares.length === 0 && (
              <div className="px-3 py-2 text-xs text-muted-foreground">Nenhum titular. Cadastre em Configuracoes.</div>
            )}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Descricao</Label>
        <Textarea {...form.register("descricao")} placeholder="Descricao (opcional)" data-testid="input-despesa-descricao" />
      </div>
    </>
  );

  const despesaColumns = [
    { key: "banco", label: "Banco" },
    { key: "data", label: "Data" },
    { key: "valor", label: "Valor", format: (v) => formatCurrency(v) },
    { key: "forma_pagamento", label: "Pagamento" },
    { key: "categoria", label: "Categoria" },
    { key: "titular", label: "Titular" },
    { key: "descricao", label: "Descricao" },
  ];

  return (
    <div className="space-y-6" data-testid="hub-pessoal-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-purple-500/10">
            <UserCircle className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Hub Pessoal</h2>
            <p className="text-sm text-muted-foreground">Controle de despesas pessoais</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setDConfigBancos(true)} data-testid="config-bancos-btn">
            <Settings className="w-4 h-4 mr-1" /> Bancos
          </Button>
          <Button variant="outline" size="sm" onClick={() => setDConfigTitulares(true)} data-testid="config-titulares-btn">
            <Settings className="w-4 h-4 mr-1" /> Titulares
          </Button>
          <Button variant="outline" size="sm" onClick={() => setDConfig(true)} data-testid="config-categorias-btn">
            <Settings className="w-4 h-4 mr-1" /> Categorias
          </Button>
          <Button variant="outline" size="icon" onClick={handleMesAnterior} data-testid="prev-month-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
            <SelectTrigger className="w-[130px]" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MESES.map((m, i) => (
                <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
            <SelectTrigger className="w-[90px]" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ANOS.map((a) => (
                <SelectItem key={a} value={String(a)}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={handleMesProximo} data-testid="next-month-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={fetchAll} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card className="border-orange-500/20 bg-orange-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="w-4 h-4 text-orange-400" />
                <span className="text-xs text-muted-foreground">Despesas Variaveis</span>
              </div>
              <p className="text-lg font-bold text-orange-400" data-testid="stat-variaveis">{formatCurrency(stats.total_variaveis)}</p>
            </CardContent>
          </Card>
          <Card className="border-violet-500/20 bg-violet-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Receipt className="w-4 h-4 text-violet-400" />
                <span className="text-xs text-muted-foreground">Despesas Fixas</span>
              </div>
              <p className="text-lg font-bold text-violet-400" data-testid="stat-fixas">{formatCurrency(stats.total_fixas)}</p>
            </CardContent>
          </Card>
          <Card className="border-red-500/20 bg-red-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <ArrowDownRight className="w-4 h-4 text-red-400" />
                <span className="text-xs text-muted-foreground">Total Saidas</span>
              </div>
              <p className="text-lg font-bold text-red-400" data-testid="stat-total-saidas">{formatCurrency(stats.total_saidas)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Grafico Titulares - Quem gastou mais */}
      {!loading && (() => {
        const allDespesas = [...variaveis, ...fixas];
        const titularMap = {};
        allDespesas.forEach((d) => {
          const t = d.titular || "Sem titular";
          titularMap[t] = (titularMap[t] || 0) + (d.valor || 0);
        });
        const chartData = Object.entries(titularMap)
          .map(([nome, total]) => ({ nome, total: Math.round(total * 100) / 100 }))
          .sort((a, b) => b.total - a.total);
        const COLORS = ["#f97316", "#a855f7", "#ef4444", "#3b82f6", "#10b981", "#f59e0b", "#ec4899", "#06b6d4"];
        if (chartData.length === 0) return null;
        return (
          <Card className="border-border/60" data-testid="chart-titulares">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <UserCircle className="w-5 h-5 text-purple-400" />
                Gastos por Titular
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 45)}>
                <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis type="number" tickFormatter={(v) => `R$${v}`} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis type="category" dataKey="nome" width={120} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    formatter={(v) => [formatCurrency(v), "Total"]}
                    contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", color: "#ffffff" }}
                    labelStyle={{ color: "#ffffff" }}
                    itemStyle={{ color: "#ffffff" }}
                  />
                  <Bar dataKey="total" radius={[0, 6, 6, 0]} maxBarSize={32}>
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              {/* Filtro de titular - clique para filtrar o detalhamento */}
              <div className="mt-4 pt-3 border-t border-border/40">
                <p className="text-xs text-muted-foreground mb-2">Filtrar detalhamento por titular:</p>
                <div className="flex flex-wrap gap-2" data-testid="filtro-titular-botoes">
                  <Button
                    type="button"
                    size="sm"
                    variant={filtroTitular === "" ? "default" : "outline"}
                    onClick={() => setFiltroTitular("")}
                    data-testid="filtro-titular-todos"
                  >
                    Todos
                  </Button>
                  {chartData.map((c, i) => (
                    <Button
                      key={c.nome}
                      type="button"
                      size="sm"
                      variant={filtroTitular === c.nome ? "default" : "outline"}
                      onClick={() => setFiltroTitular(c.nome)}
                      data-testid={`filtro-titular-${c.nome}`}
                      style={filtroTitular === c.nome ? { backgroundColor: COLORS[i % COLORS.length], borderColor: COLORS[i % COLORS.length] } : { borderColor: COLORS[i % COLORS.length], color: COLORS[i % COLORS.length] }}
                    >
                      {c.nome}
                    </Button>
                  ))}
                </div>
                {filtroTitular && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Exibindo apenas despesas de <span className="font-semibold text-primary">{filtroTitular}</span>
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <Card className="border-border/60">
          <CardContent className="p-4 pt-4">
            <Tabs defaultValue="variaveis" className="w-full">
              <TabsList className="w-full grid grid-cols-2 mb-4" data-testid="pessoal-tabs">
                <TabsTrigger value="variaveis" className="gap-1.5" data-testid="tab-variaveis">
                  <Wallet className="w-4 h-4" /> Despesas Variaveis
                </TabsTrigger>
                <TabsTrigger value="fixas" className="gap-1.5" data-testid="tab-fixas">
                  <Receipt className="w-4 h-4" /> Despesas Fixas
                </TabsTrigger>
              </TabsList>

              {/* Tab: Despesas Variaveis */}
              <TabsContent value="variaveis">
                {(() => {
                  const filtraTitular = (arr) => filtroTitular
                    ? arr.filter(d => (d.titular || "Sem titular") === filtroTitular)
                    : arr;
                  const variaveisFiltered = filtraTitular(variaveis);
                  return (
                    <>
                <div className="flex justify-end mb-3">
                  <Button size="sm" onClick={() => { setEditVariavel(null); fVariavel.reset({ banco: "", data: TODAY, valor: 0, forma_pagamento: "", categoria: "", titular: "", descricao: "" }); setDVariavel(true); }} data-testid="add-variavel-btn">
                    <Plus className="w-4 h-4 mr-1" /> Adicionar
                  </Button>
                </div>
                {variaveisFiltered.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8 text-sm">
                    {filtroTitular ? `Nenhuma despesa variavel de ${filtroTitular}` : "Nenhum registro"}
                  </p>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {despesaColumns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
                            <TableHead className="w-[80px]">Acoes</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {variaveisFiltered.map((item) => (
                            <TableRow key={item.id}>
                              {despesaColumns.map((c) => (
                                <TableCell key={c.key}>
                                  {c.key === "categoria" && item[c.key] ? (
                                    <Badge variant="outline" className="text-xs">{item[c.key]}</Badge>
                                  ) : c.format ? c.format(item[c.key]) : item[c.key]}
                                </TableCell>
                              ))}
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditVariavel(item); fVariavel.reset(item); setDVariavel(true); }}>
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => deleteDespesa("variaveis", item.id)}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="mt-3 p-3 rounded-lg bg-red-500/10 text-red-400 flex justify-between items-center text-sm font-semibold">
                      <span>Total{filtroTitular ? ` (${filtroTitular})` : ""}:</span>
                      <span>{formatCurrency(variaveisFiltered.reduce((acc, d) => acc + (d.valor || 0), 0))}</span>
                    </div>
                  </>
                )}
                    </>
                  );
                })()}
              </TabsContent>

              {/* Tab: Despesas Fixas */}
              <TabsContent value="fixas">
                {(() => {
                  const filtraTitular = (arr) => filtroTitular
                    ? arr.filter(d => (d.titular || "Sem titular") === filtroTitular)
                    : arr;
                  const fixasFiltered = filtraTitular(fixas);
                  return (
                    <>
                <div className="flex justify-end mb-3">
                  <Button size="sm" onClick={() => { setEditFixa(null); fFixa.reset({ banco: "", data: TODAY, valor: 0, forma_pagamento: "", categoria: "", titular: "", descricao: "" }); setDFixa(true); }} data-testid="add-fixa-btn">
                    <Plus className="w-4 h-4 mr-1" /> Adicionar
                  </Button>
                </div>
                {fixasFiltered.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8 text-sm">
                    {filtroTitular ? `Nenhuma despesa fixa de ${filtroTitular}` : "Nenhum registro"}
                  </p>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {despesaColumns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
                            <TableHead className="w-[80px]">Acoes</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {fixasFiltered.map((item) => (
                            <TableRow key={item.id}>
                              {despesaColumns.map((c) => (
                                <TableCell key={c.key}>
                                  {c.key === "categoria" && item[c.key] ? (
                                    <Badge variant="outline" className="text-xs">{item[c.key]}</Badge>
                                  ) : c.format ? c.format(item[c.key]) : item[c.key]}
                                </TableCell>
                              ))}
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditFixa(item); fFixa.reset(item); setDFixa(true); }}>
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => deleteDespesa("fixas", item.id)}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="mt-3 p-3 rounded-lg bg-red-500/10 text-red-400 flex justify-between items-center text-sm font-semibold">
                      <span>Total{filtroTitular ? ` (${filtroTitular})` : ""}:</span>
                      <span>{formatCurrency(fixasFiltered.reduce((acc, d) => acc + (d.valor || 0), 0))}</span>
                    </div>
                  </>
                )}
                    </>
                  );
                })()}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Dialog Despesa Variavel */}
      <Dialog open={dVariavel} onOpenChange={setDVariavel}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-variavel">
          <DialogHeader>
            <DialogTitle>{editVariavel ? "Editar" : "Nova"} Despesa Variavel</DialogTitle>
          </DialogHeader>
          <form onSubmit={fVariavel.handleSubmit((data) => submitDespesa("variaveis", data, editVariavel, setDVariavel))} className="space-y-4">
            {renderDespesaForm(fVariavel)}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDVariavel(false)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} data-testid="submit-variavel-btn">{submitting ? "Salvando..." : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Despesa Fixa */}
      <Dialog open={dFixa} onOpenChange={setDFixa}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-fixa">
          <DialogHeader>
            <DialogTitle>{editFixa ? "Editar" : "Nova"} Despesa Fixa</DialogTitle>
          </DialogHeader>
          <form onSubmit={fFixa.handleSubmit((data) => submitDespesa("fixas", data, editFixa, setDFixa))} className="space-y-4">
            {renderDespesaForm(fFixa)}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDFixa(false)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} data-testid="submit-fixa-btn">{submitting ? "Salvando..." : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Configuracao de Categorias */}
      <Dialog open={dConfig} onOpenChange={setDConfig}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-categorias">
          <DialogHeader>
            <DialogTitle>Configurar Categorias</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <form onSubmit={fCategoria.handleSubmit(submitCategoria)} className="flex gap-2">
              <Input {...fCategoria.register("nome")} placeholder="Nova categoria..." className="flex-1" data-testid="input-nova-categoria" />
              <Button type="submit" size="sm" data-testid="add-categoria-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </form>
            {fCategoria.formState.errors.nome && <p className="text-xs text-destructive">{fCategoria.formState.errors.nome.message}</p>}
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {categorias.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhuma categoria criada</p>
              ) : (
                categorias.map((cat) => (
                  <div key={cat.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                    <span className="text-sm font-medium">{cat.nome}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => deleteCategoria(cat.id)} data-testid={`delete-cat-${cat.id}`}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Config Bancos Pessoal */}
      <Dialog open={dConfigBancos} onOpenChange={setDConfigBancos}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-config-bancos-pessoal">
          <DialogHeader><DialogTitle>Configurar Bancos</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!novoBanco.trim()) return;
              try {
                await authAxios.post("/hub-pessoal/config/bancos", { nome: novoBanco.trim() });
                toast.success("Banco criado!");
                setNovoBanco("");
                const res = await authAxios.get("/hub-pessoal/config/bancos");
                setBancosPessoal(res.data);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Erro ao criar banco");
              }
            }} className="flex gap-2">
              <Input value={novoBanco} onChange={(e) => setNovoBanco(e.target.value)} placeholder="Nome do banco..." className="flex-1" data-testid="input-novo-banco-pessoal" />
              <Button type="submit" size="sm" data-testid="add-banco-pessoal-btn"><Plus className="w-4 h-4" /></Button>
            </form>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {bancosPessoal.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhum banco cadastrado</p>
              ) : (
                bancosPessoal.map((b) => (
                  <div key={b.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                    <span className="text-sm font-medium">{b.nome}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={async () => {
                      if (!window.confirm("Excluir este banco?")) return;
                      try {
                        await authAxios.delete(`/hub-pessoal/config/bancos/${b.id}`);
                        toast.success("Banco removido!");
                        const res = await authAxios.get("/hub-pessoal/config/bancos");
                        setBancosPessoal(res.data);
                      } catch { toast.error("Erro ao remover"); }
                    }} data-testid={`delete-banco-pessoal-${b.id}`}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Config Titulares */}
      <Dialog open={dConfigTitulares} onOpenChange={setDConfigTitulares}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-config-titulares">
          <DialogHeader><DialogTitle>Configurar Titulares</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!novoTitular.trim()) return;
              try {
                await authAxios.post("/hub-pessoal/config/titulares", { nome: novoTitular.trim() });
                toast.success("Titular criado!");
                setNovoTitular("");
                const res = await authAxios.get("/hub-pessoal/config/titulares");
                setTitulares(res.data);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Erro ao criar titular");
              }
            }} className="flex gap-2">
              <Input value={novoTitular} onChange={(e) => setNovoTitular(e.target.value)} placeholder="Nome do titular..." className="flex-1" data-testid="input-novo-titular" />
              <Button type="submit" size="sm" data-testid="add-titular-btn"><Plus className="w-4 h-4" /></Button>
            </form>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {titulares.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhum titular cadastrado</p>
              ) : (
                titulares.map((t) => (
                  <div key={t.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                    <span className="text-sm font-medium">{t.nome}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={async () => {
                      if (!window.confirm("Excluir este titular?")) return;
                      try {
                        await authAxios.delete(`/hub-pessoal/config/titulares/${t.id}`);
                        toast.success("Titular removido!");
                        const res = await authAxios.get("/hub-pessoal/config/titulares");
                        setTitulares(res.data);
                      } catch { toast.error("Erro ao remover"); }
                    }} data-testid={`delete-titular-${t.id}`}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
